import { AppDispatch } from 'app/store';
import { LevelType, PositionAndDimensionsType, SpaceType } from 'data/types';
import { beginUndoBatch, endUndoBatch, setStructure } from 'features/structure';
import { produce } from 'immer';
import { useCallback, useEffect, useRef } from 'react';
import {
  createSection,
  duplicateSpace,
  findMinimalMoveToAvoidOverlap
} from 'utils/structure';

const PASTE_OFFSET = 2;

type ClipboardData = {
  spaces: SpaceType[];
  originX: number;
  originY: number;
};

function findSpacesByIds(
  structure: LevelType[],
  levelIndex: number,
  spaceIds: Set<string>
): SpaceType[] {
  const level = structure[levelIndex];
  if (!level) return [];

  const result: SpaceType[] = [];
  level.sections.forEach((section) => {
    section.spaces.forEach((space) => {
      if (spaceIds.has(space.uniqueId)) {
        result.push(space);
      }
    });
  });
  return result;
}

function getBoundingBox(spaces: SpaceType[]): { minX: number; minY: number } {
  let minX = Infinity;
  let minY = Infinity;
  spaces.forEach((space) => {
    if (space.position) {
      minX = Math.min(minX, space.position.x);
      minY = Math.min(minY, space.position.y);
    }
  });
  return { minX, minY };
}

export function useSpaceClipboard({
  enabled,
  selectedSpaceIds,
  structure,
  activeLevelIndex,
  dispatch,
  setSelectedSpaceIds
}: {
  enabled: boolean;
  selectedSpaceIds: Set<string>;
  structure: LevelType[];
  activeLevelIndex: number;
  dispatch: AppDispatch;
  setSelectedSpaceIds: React.Dispatch<React.SetStateAction<Set<string>>>;
}) {
  const clipboardRef = useRef<ClipboardData | null>(null);
  const selectedSpaceIdsRef = useRef(selectedSpaceIds);
  selectedSpaceIdsRef.current = selectedSpaceIds;

  const structureRef = useRef(structure);
  structureRef.current = structure;

  const activeLevelIndexRef = useRef(activeLevelIndex);
  activeLevelIndexRef.current = activeLevelIndex;

  const handleCopy = useCallback(() => {
    const currentIds = selectedSpaceIdsRef.current;
    if (currentIds.size === 0) return;

    const spaces = findSpacesByIds(
      structureRef.current,
      activeLevelIndexRef.current,
      currentIds
    );

    if (spaces.length === 0) return;

    const { minX, minY } = getBoundingBox(spaces);
    const clonedSpaces = structuredClone(spaces);

    clipboardRef.current = {
      spaces: clonedSpaces,
      originX: minX,
      originY: minY
    };
  }, []);

  const handlePaste = useCallback(() => {
    const clipboard = clipboardRef.current;
    if (!clipboard || clipboard.spaces.length === 0) return;

    const currentStructure = structureRef.current;
    const levelIndex = activeLevelIndexRef.current;
    const level = currentStructure[levelIndex];
    if (!level) return;

    const pastedSpaces = clipboard.spaces.map((space) =>
      duplicateSpace(structuredClone(space), true)
    );

    pastedSpaces.forEach((space) => {
      if (space.position) {
        // eslint-disable-next-line no-param-reassign
        space.position = {
          x: space.position.x + PASTE_OFFSET,
          y: space.position.y + PASTE_OFFSET
        };
      }
      if (space.width && space.length) {
        const sqFtAttr = space.attributes.find(
          (a) => a.code === 'square_footage'
        );
        if (sqFtAttr) {
          sqFtAttr.value = Math.ceil(space.width * space.length);
        }
      }
    });

    const existingSpaces: PositionAndDimensionsType[] = level.sections
      .flatMap((section) => section.spaces)
      .filter((space) => space.position && space.width && space.length)
      .map((space) => ({
        x: space.position!.x,
        y: space.position!.y,
        width: space.width!,
        height: space.length!
      }));

    const pastedDimensions: PositionAndDimensionsType[] = pastedSpaces
      .filter((space) => space.position && space.width && space.length)
      .map((space) => ({
        x: space.position!.x,
        y: space.position!.y,
        width: space.width!,
        height: space.length!
      }));

    const adjustment = findMinimalMoveToAvoidOverlap(
      pastedDimensions,
      existingSpaces
    );

    pastedSpaces.forEach((space) => {
      if (space.position) {
        // eslint-disable-next-line no-param-reassign
        space.position = {
          x: Math.max(0, space.position.x + adjustment.deltaX),
          y: Math.max(0, space.position.y + adjustment.deltaY)
        };
      }
    });

    dispatch(beginUndoBatch());

    const newStructure = produce(currentStructure, (draft) => {
      pastedSpaces.forEach((space) => {
        const newSection = createSection({ spaces: [space] });
        draft[levelIndex].sections.push(newSection);
      });
    });

    dispatch(setStructure(newStructure));
    dispatch(endUndoBatch());

    const newIds = new Set(pastedSpaces.map((s) => s.uniqueId));
    setSelectedSpaceIds(newIds);
  }, [dispatch, setSelectedSpaceIds]);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInput =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable;
      if (isInput) return;

      const isCtrlOrCmd = e.ctrlKey || e.metaKey;
      if (!isCtrlOrCmd) return;

      if (e.key === 'c') {
        e.preventDefault();
        handleCopy();
      } else if (e.key === 'v') {
        e.preventDefault();
        handlePaste();
      }
    };

    globalThis.addEventListener('keydown', handleKeyDown);

    // eslint-disable-next-line consistent-return
    return () => {
      globalThis.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, handleCopy, handlePaste]);
}
